/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {FocusableOption, FocusKeyManager} from '@angular/cdk/a11y';
import {coerceBooleanProperty} from '@angular/cdk/coercion';
import {SelectionModel} from '@angular/cdk/collections';
import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  forwardRef,
  Inject,
  Input,
  OnDestroy,
  Optional,
  Output,
  QueryList,
  Renderer2,
  ViewEncapsulation,
} from '@angular/core';
import {
  CanDisable,
  CanDisableRipple,
  MATERIAL_COMPATIBILITY_MODE,
  MdLine,
  MdLineSetter,
  mixinDisabled,
  mixinDisableRipple,
  RxChain,
  SPACE,
  startWith,
  switchMap,
} from '@angular/material/core';
import {merge} from 'rxjs/observable/merge';
import {Subscription} from 'rxjs/Subscription';
/**
 * \@docs-private
 */
export class MdSelectionListBase {}
export const /** @type {?} */ _MdSelectionListMixinBase = mixinDisableRipple(mixinDisabled(MdSelectionListBase));
/**
 * \@docs-private
 */
export class MdListOptionBase {}
export const /** @type {?} */ _MdListOptionMixinBase = mixinDisableRipple(MdListOptionBase);

/** Event emitted by a selection-list whenever the state of an option is changed. */
export interface MdSelectionListOptionEvent {
  option: MdListOption;
}

const /** @type {?} */ FOCUSED_STYLE: string = 'mat-list-item-focus';
/**
 * Component for list-options of selection-list. Each list-option can automatically
 * generate a checkbox and can put current item into the selectionModel of selection-list
 * if the current item is checked.
 */
export class MdListOption extends _MdListOptionMixinBase
    implements AfterContentInit, OnDestroy, FocusableOption, CanDisableRipple {
private _lineSetter: MdLineSetter;
private _selected: boolean = false;
private _disabled: boolean = false;
/**
 * Whether the option has focus.
 */
_hasFocus: boolean = false;

   _lines: QueryList<MdLine>;
/**
 * Whether the label should appear before or after the checkbox. Defaults to 'after'
 */
checkboxPosition: 'before' | 'after' = 'after';
/**
 * Value of the option
 */
value: any;
/**
 * Whether the option is disabled.
 * @return {?}
 */
get disabled() { return (this.selectionList && this.selectionList.disabled) || this._disabled; }
/**
 * @param {?} value
 * @return {?}
 */
set disabled(value: any) { this._disabled = coerceBooleanProperty(value); }
/**
 * Whether the option is selected.
 * @return {?}
 */
get selected() { return this._selected; }
/**
 * @param {?} value
 * @return {?}
 */
set selected(value: boolean) { this._selected = coerceBooleanProperty(value); }
/**
 * Emitted when the option is focused.
 */
onFocus = new EventEmitter<MdSelectionListOptionEvent>();
/**
 * Emitted when the option is selected.
 */
selectChange = new EventEmitter<MdSelectionListOptionEvent>();
/**
 * Emitted when the option is deselected.
 */
deselected = new EventEmitter<MdSelectionListOptionEvent>();
/**
 * Emitted when the option is destroyed.
 */
destroyed = new EventEmitter<MdSelectionListOptionEvent>();
/**
 * @param {?} _renderer
 * @param {?} _element
 * @param {?} _changeDetector
 * @param {?} selectionList
 */
constructor(private _renderer: Renderer2,
private _element: ElementRef,
private _changeDetector: ChangeDetectorRef,
public selectionList: MdSelectionList) {
    super();
  }
/**
 * @return {?}
 */
ngAfterContentInit() {
    this._lineSetter = new MdLineSetter(this._lines, this._renderer, this._element);

    if (this.selectionList.disabled) {
      this.disabled = true;
    }
  }
/**
 * @return {?}
 */
ngOnDestroy(): void {
    this.destroyed.emit({option: this});
  }
/**
 * Toggles the selection state of the option.
 * @return {?}
 */
toggle(): void {
    this.selected = !this.selected;
    this.selectionList.selectedOptions.toggle(this);
    this._changeDetector.markForCheck();
  }
/**
 * Allows for programmatic focusing of the option.
 * @return {?}
 */
focus(): void {
    this._element.nativeElement.focus();
    this.onFocus.emit({option: this});
  }
/**
 * Whether this list item should show a ripple effect when clicked.
 * @return {?}
 */
_isRippleDisabled() {
    return this.disabled || this.disableRipple || this.selectionList.disableRipple;
  }
/**
 * @return {?}
 */
_handleClick() {
    if (!this.disabled) {
      this.toggle();
    }
  }
/**
 * @return {?}
 */
_handleFocus() {
    this._hasFocus = true;
    this._renderer.addClass(this._element.nativeElement, FOCUSED_STYLE);
  }
/**
 * @return {?}
 */
_handleBlur() {
    this._renderer.removeClass(this._element.nativeElement, FOCUSED_STYLE);
  }
/**
 * Retrieves the DOM element of the component host.
 * @return {?}
 */
_getHostElement(): HTMLElement {
    return this._element.nativeElement;
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  moduleId: module.id,
  selector: 'md-list-option, mat-list-option',
  inputs: ['disableRipple'],
  host: {
    'role': 'option',
    'class': 'mat-list-item mat-list-option',
    '(focus)': '_handleFocus()',
    '(blur)': '_handleBlur()',
    '(click)': '_handleClick()',
    'tabindex': '-1',
    '[class.mat-list-item-disabled]': 'disabled',
    '[attr.aria-selected]': 'selected.toString()',
    '[attr.aria-disabled]': 'disabled.toString()',
  },
  templateUrl: 'list-option.html',
  encapsulation: ViewEncapsulation.None,
  preserveWhitespaces: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [{provide: MATERIAL_COMPATIBILITY_MODE, useValue: true}],
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: Renderer2, },
{type: ElementRef, },
{type: ChangeDetectorRef, },
{type: MdSelectionList, decorators: [{ type: Optional }, { type: Inject, args: [forwardRef(() => MdSelectionList), ] }, ]},
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'_lines': [{ type: ContentChildren, args: [MdLine, ] },],
'checkboxPosition': [{ type: Input },],
'value': [{ type: Input },],
'disabled': [{ type: Input },],
'selected': [{ type: Input },],
'selectChange': [{ type: Output },],
'deselected': [{ type: Output },],
'destroyed': [{ type: Output },],
};
}

function MdListOption_tsickle_Closure_declarations() {
/** @type {?} */
MdListOption.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdListOption.ctorParameters;
/** @type {?} */
MdListOption.propDecorators;
/** @type {?} */
MdListOption.prototype._lineSetter;
/** @type {?} */
MdListOption.prototype._selected;
/** @type {?} */
MdListOption.prototype._disabled;
/**
 * Whether the option has focus.
 * @type {?}
 */
MdListOption.prototype._hasFocus;
/** @type {?} */
MdListOption.prototype._lines;
/**
 * Whether the label should appear before or after the checkbox. Defaults to 'after'
 * @type {?}
 */
MdListOption.prototype.checkboxPosition;
/**
 * Value of the option
 * @type {?}
 */
MdListOption.prototype.value;
/**
 * Emitted when the option is focused.
 * @type {?}
 */
MdListOption.prototype.onFocus;
/**
 * Emitted when the option is selected.
 * @type {?}
 */
MdListOption.prototype.selectChange;
/**
 * Emitted when the option is deselected.
 * @type {?}
 */
MdListOption.prototype.deselected;
/**
 * Emitted when the option is destroyed.
 * @type {?}
 */
MdListOption.prototype.destroyed;
/** @type {?} */
MdListOption.prototype._renderer;
/** @type {?} */
MdListOption.prototype._element;
/** @type {?} */
MdListOption.prototype._changeDetector;
/** @type {?} */
MdListOption.prototype.selectionList;
}

/**
 * Material Design list component where each item is a selectable option. Behaves as a listbox.
 */
export class MdSelectionList extends _MdSelectionListMixinBase
    implements FocusableOption, CanDisable, CanDisableRipple, AfterContentInit, OnDestroy {
/**
 * Tab index for the selection-list.
 */
_tabIndex = 0;
/**
 * Subscription to all list options' onFocus events
 */
private _optionFocusSubscription = Subscription.EMPTY;
/**
 * Subscription to all list options' destroy events
 */
private _optionDestroyStream = Subscription.EMPTY;
/**
 * The FocusKeyManager which handles focus.
 */
_keyManager: FocusKeyManager<MdListOption>;
/**
 * The option components contained within this selection-list.
 */
options: QueryList<MdListOption>;
/**
 * The currently selected options.
 */
selectedOptions: SelectionModel<MdListOption> = new SelectionModel<MdListOption>(true);
/**
 * @param {?} _element
 */
constructor(private _element: ElementRef) {
    super();
  }
/**
 * @return {?}
 */
ngAfterContentInit(): void {
    this._keyManager = new FocusKeyManager<MdListOption>(this.options).withWrap();

    if (this.disabled) {
      this._tabIndex = -1;
    }

    this._optionFocusSubscription = this._onFocusSubscription();
    this._optionDestroyStream = this._onDestroySubscription();
  }
/**
 * @return {?}
 */
ngOnDestroy(): void {
    this._optionDestroyStream.unsubscribe();
    this._optionFocusSubscription.unsubscribe();
  }
/**
 * Focus the selection-list.
 * @return {?}
 */
focus() {
    this._element.nativeElement.focus();
  }
/**
 * Selects all of the options.
 * @return {?}
 */
selectAll() {
    this.options.forEach(option => {
      if (!option.selected) {
        option.toggle();
      }
    });
  }
/**
 * Deselects all of the options.
 * @return {?}
 */
deselectAll() {
    this.options.forEach(option => {
      if (option.selected) {
        option.toggle();
      }
    });
  }
/**
 * Map all the options' destroy event subscriptions and merge them into one stream.
 * @return {?}
 */
private _onDestroySubscription(): Subscription {
    return RxChain.from(this.options.changes)
      .call(startWith, this.options)
      .call(switchMap, (options: MdListOption[]) => {
        return merge(...options.map(option => option.destroyed));
      }).subscribe((e: MdSelectionListOptionEvent) => {
        let /** @type {?} */ optionIndex: number = this.options.toArray().indexOf(e.option);
        if (e.option._hasFocus) {
          // Check whether the option is the last item
          if (optionIndex < this.options.length - 1) {
            this._keyManager.setActiveItem(optionIndex);
          } else if (optionIndex - 1 >= 0) {
            this._keyManager.setActiveItem(optionIndex - 1);
          }
        }
        e.option.destroyed.unsubscribe();
      });
  }
/**
 * Map all the options' onFocus event subscriptions and merge them into one stream.
 * @return {?}
 */
private _onFocusSubscription(): Subscription {
    return RxChain.from(this.options.changes)
      .call(startWith, this.options)
      .call(switchMap, (options: MdListOption[]) => {
        return merge(...options.map(option => option.onFocus));
      }).subscribe((e: MdSelectionListOptionEvent) => {
      let /** @type {?} */ optionIndex: number = this.options.toArray().indexOf(e.option);
      this._keyManager.updateActiveItemIndex(optionIndex);
    });
  }
/**
 * Passes relevant key presses to our key manager.
 * @param {?} event
 * @return {?}
 */
_keydown(event: KeyboardEvent) {
    switch (event.keyCode) {
      case SPACE:
        this._toggleSelectOnFocusedOption();
        // Always prevent space from scrolling the page since the list has focus
        event.preventDefault();
        break;
      default:
        this._keyManager.onKeydown(event);
    }
  }
/**
 * Toggles the selected state of the currently focused option.
 * @return {?}
 */
private _toggleSelectOnFocusedOption(): void {
    let /** @type {?} */ focusedIndex = this._keyManager.activeItemIndex;

    if (focusedIndex != null && this._isValidIndex(focusedIndex)) {
      let /** @type {?} */ focusedOption: MdListOption = this.options.toArray()[focusedIndex];

      if (focusedOption) {
        focusedOption.toggle();
      }
    }
  }
/**
 * Utility to ensure all indexes are valid.
 * 
 * @param {?} index The index to be checked.
 * @return {?} True if the index is valid for our list of options.
 */
private _isValidIndex(index: number): boolean {
    return index >= 0 && index < this.options.length;
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  moduleId: module.id,
  selector: 'md-selection-list, mat-selection-list',
  inputs: ['disabled', 'disableRipple'],
  host: {
    'role': 'listbox',
    '[attr.tabindex]': '_tabIndex',
    'class': 'mat-selection-list',
    '(focus)': 'focus()',
    '(keydown)': '_keydown($event)',
    '[attr.aria-disabled]': 'disabled.toString()'},
  template: '<ng-content></ng-content>',
  styleUrls: ['list.css'],
  encapsulation: ViewEncapsulation.None,
  preserveWhitespaces: false,
  changeDetection: ChangeDetectionStrategy.OnPush
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ElementRef, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'options': [{ type: ContentChildren, args: [MdListOption, ] },],
};
}

function MdSelectionList_tsickle_Closure_declarations() {
/** @type {?} */
MdSelectionList.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdSelectionList.ctorParameters;
/** @type {?} */
MdSelectionList.propDecorators;
/**
 * Tab index for the selection-list.
 * @type {?}
 */
MdSelectionList.prototype._tabIndex;
/**
 * Subscription to all list options' onFocus events
 * @type {?}
 */
MdSelectionList.prototype._optionFocusSubscription;
/**
 * Subscription to all list options' destroy events
 * @type {?}
 */
MdSelectionList.prototype._optionDestroyStream;
/**
 * The FocusKeyManager which handles focus.
 * @type {?}
 */
MdSelectionList.prototype._keyManager;
/**
 * The option components contained within this selection-list.
 * @type {?}
 */
MdSelectionList.prototype.options;
/**
 * The currently selected options.
 * @type {?}
 */
MdSelectionList.prototype.selectedOptions;
/** @type {?} */
MdSelectionList.prototype._element;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
